import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog' 
import { Row } from '@tanstack/react-table'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/components/ui/use-toast'
import { isAxiosError } from '@/api/business'
import { UserTable } from '../..'
import { resetPasswordUser } from '@/api/business/user'
import { UserKeys } from '@/queries/useUserQuery'
import Button from '@/components/Button'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form' 
import { Input } from '@/components/ui/input'
import { useEffect, useState } from 'react' 
import { Copy, KeyRound, RefreshCw } from 'lucide-react' 

interface ResetPasswordDialogProps {
    row: Row<UserTable>
    isOpen: boolean
    onClose: () => void
}

const ResetPasswordDialog = ({ row, isOpen, onClose }: ResetPasswordDialogProps) => {
    const [showGeneratedPassword, setShowGeneratedPassword] = useState(false);
    const [generatedPassword, setGeneratedPassword] = useState('');
    const queryClient = useQueryClient()
    const { toast } = useToast()

    const schema = z.object({
        DS_SENHA: z.string()
            .min(8, { message: "A senha deve ter pelo menos 8 caracteres" })
            .regex(/[A-Z]/, { message: "A senha deve conter pelo menos uma letra maiúscula" })
            .regex(/[a-z]/, { message: "A senha deve conter pelo menos uma letra minúscula" })
            .regex(/[0-9]/, { message: "A senha deve conter pelo menos um número" })
            .regex(/[\W_]/, { message: "A senha deve conter pelo menos um caractere especial" }),
        CONFIRMAR_SENHA: z.string().min(1, {
            message: 'O campo Confirmar Senha é obrigatório',
        }),
    }).refine((data) => data.DS_SENHA === data.CONFIRMAR_SENHA, {
        path: ["CONFIRMAR_SENHA"], 
        message: "As senhas não conferem",
    });

    const form = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        defaultValues: { 
            DS_SENHA: "", 
            CONFIRMAR_SENHA: "" 
        },
    })

    useEffect(() => {
        setGeneratedPassword('')
        setShowGeneratedPassword(false)

        form.reset({
            DS_SENHA: "", 
            CONFIRMAR_SENHA: "" 
        })
    }, [row.original])

    const { mutate, isPending: mutateLoading } = useMutation({
        mutationFn: resetPasswordUser,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: UserKeys.lists() })
            toast({
                title: 'Usuário atualizado com sucesso',
            })
            onClose()
        },
        onError: (err) => {
            let errorMessage =
                'Não foi possível editar o usuário. Tente novamente mais tarde.'

            if (isAxiosError(err)) {
                errorMessage = err.response?.data.message || errorMessage
            }

            toast({
                title: 'Erro ao editar usuário',
                description: errorMessage,
                variant: 'destructive',
            })
        },
    })

    const onSubmit = (data: z.infer<typeof schema>) => {
        mutate({
            SK_USUARIO: row.original.SK_USUARIO,
           ...data
        })
    }

    const generateRandomPassword = () => {
        const length = 12;
        const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%&*";
        let password = "";
         
        password += "ABCDEFGHIJKLMNOPQRSTUVWXYZ"[Math.floor(Math.random() * 26)]; // Uppercase
        password += "abcdefghijklmnopqrstuvwxyz"[Math.floor(Math.random() * 26)]; // Lowercase
        password += "0123456789"[Math.floor(Math.random() * 10)]; // Number
        password += "!@#$%&*"[Math.floor(Math.random() * 7)]; // Special char
         
        for (let i = 4; i < length; i++) {
        password += charset[Math.floor(Math.random() * charset.length)];
        } 
        return password.split('').sort(() => Math.random() - 0.5).join('');
    };

    const copyToClipboard = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
             toast({
                title: 'Senha copiada para a área de transferência',
            }) 
        } catch (err) {
             toast({
                title: 'Erro ao copiar',
                description: 'Não foi possível copiar a senha',
                variant: 'destructive',
            }) 
        }
    };

    const handleGeneratePassword = () => {
        const password = generateRandomPassword();
        setGeneratedPassword(password)
        setShowGeneratedPassword(true)
        form.setValue('DS_SENHA',password)
        form.setValue('CONFIRMAR_SENHA',password)
        toast({
            title: 'Uma nova senha foi gerada automaticamente. Copie e envie para o usuário.',
        }) 
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className='max-w-[700px] overflow-auto'>
                <DialogHeader className='px-2'>
                    <DialogTitle>Editar Revenda</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className="space-y-2 px-2 overflow-hidden overflow-y-auto"
                    >
                        <div  className='grid  gap-4'>
                            <div className="flex items-center justify-between mb-3"> 
                                <button
                                    type="button"
                                    onClick={handleGeneratePassword}
                                    className="px-3 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 text-sm"
                                >
                                    <RefreshCw className="w-4 h-4" />
                                    <span>Gerar senha automática</span>
                                </button>
                            </div>
                            

                            {showGeneratedPassword && generatedPassword && (
                                <div className="bg-white p-3 rounded-lg border border-blue-200">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-2">
                                            <KeyRound className="w-4 h-4 text-blue-600" />
                                            <span className="font-mono text-sm font-medium text-blue-800">
                                            {generatedPassword}
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => copyToClipboard(generatedPassword)}
                                            className="p-1 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                                            title="Copiar senha"
                                        >
                                        <Copy className="w-4 h-4" />
                                        </button>
                                    </div>
                                    <p className="text-xs text-blue-600 mt-1">
                                        ⚠️ Copie esta senha e envie para o usuário de forma segura
                                    </p>
                                </div>
                            )}
                        </div>
                         <div className='grid grid-cols-[1fr,1fr] gap-4'>
                            <FormField
                                name="DS_SENHA"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Senha de acesso</FormLabel>
                                        <FormControl>
                                            <Input {...field} type='password' />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> 
                            <FormField
                                name="CONFIRMAR_SENHA"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Confirme a Senha</FormLabel>
                                        <FormControl>
                                            <Input {...field} type='password' />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />   
                        
                        </div>
                        <DialogFooter>
                            <Button
                                type="button"
                                onClick={onClose}
                                variant="ghost"
                            >
                                Cancelar
                            </Button>
                            <Button type="submit" isLoading={mutateLoading}>
                                Confirmar
                            </Button>
                        </DialogFooter> 
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}

export default ResetPasswordDialog
